//@ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputControl } from "../../molecules";
import { Button, Loading } from "../../atoms";
import {
  useGetBankList,
  useGetCurrencyCodeList,
  useMerchantBankUpdate,
  IMerchantBank,
} from "../../../hooks";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { z } from "zod";
import { useUserInfo } from "../../../store/User.state";

interface IMerchant {
  merchantId: number;
  merchantName: string;
}

const bankAccountSchema = z.object({
  currencyCode: z.string().min(1, "Para Birimi seçiniz"),
  iban: z.string().min(1, "IBAN giriniz").max(34, "IBAN en fazla 34 karakter olmalıdır"),
  bankCode: z.string().min(1, "Banka seçiniz"),
  accountOwner: z.string().min(1, "Hesap Sahibi giriniz"),
});

const formSchema = z.object({
  merchantId: z.number().min(1, "Üye İşyeri seçiniz"),
  account1: bankAccountSchema,
  account2: bankAccountSchema.partial().optional(),
  account3: bankAccountSchema.partial().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type EditMerchantBankModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  initialData: IMerchantBank | null;
};

const AccountInput = ({
  id,
  currencyCodeField,
  ibanField,
  bankCodeField,
  accountOwnerField,
  control,
  currencyCodeList,
  bankList,
  setValue,
  isDesktop,
  onRemove,
}) => {
  const ibanValue = useWatch({ control, name: ibanField });

  useEffect(() => {
    if (ibanValue && ibanValue.length >= 9) {
      const matched = bankList?.find((b) => b.value == ibanValue.substring(5, 9));
      if (matched) setValue(bankCodeField, matched.value);
    }
  }, [ibanValue]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight="bold">Hesap {id}</Typography>
        {id > 1 && (
          <MuiButton color="error" onClick={onRemove}>
            Hesabı Kaldır
          </MuiButton>
        )}
      </Stack>

      <Stack spacing={3} direction={isDesktop ? "row" : "column"}>
        <FormControl fullWidth>
          {currencyCodeList && (
            <Controller
              name={currencyCodeField}
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const selected = currencyCodeList.find((opt) => opt.value === value);
                return (
                  <Autocomplete
                    id={currencyCodeField}
                    options={currencyCodeList}
                    getOptionLabel={(o) => o.label}
                    value={selected || null}
                    onChange={(_, v) => onChange(v ? v.value : "")}
                    renderInput={(params) => (
                      <TextField {...params} label="Para Birimi" />
                    )}
                  />
                );
              }}
            />
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputControl id={ibanField} control={control} label="IBAN" />
        </FormControl>
      </Stack>

      <Stack spacing={3} direction={isDesktop ? "row" : "column"}>
        <FormControl fullWidth>
          {bankList && (
            <Controller
              name={bankCodeField}
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const selected = bankList.find((opt) => opt.value === value);
                return (
                  <Autocomplete
                    id={bankCodeField}
                    options={bankList}
                    getOptionLabel={(o) => o.label}
                    value={selected || null}
                    onChange={(_, v) => onChange(v ? v.value : "")}
                    renderInput={(params) => (
                      <TextField {...params} label="Banka Adı" />
                    )}
                  />
                );
              }}
            />
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputControl id={accountOwnerField} control={control} label="Hesap Sahibi" />
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const EditMerchantBankModal: React.FC<EditMerchantBankModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  initialData,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const setSnackbar = useSetSnackBar();
  const { mutate: merchantBankUpdate, isLoading } = useMerchantBankUpdate();
  const { data: rawCurrencyCodeList } = useGetCurrencyCodeList({});
  const { data: rawBankList } = useGetBankList({});
  const [userInfo] = useUserInfo();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    resetField,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  const [accountsCount, setAccountsCount] = useState(1);

  const currencyCodeList = useMemo(
    () =>
      rawCurrencyCodeList?.data?.map((c) => ({
        label: c.value,
        value: c.key,
      })),
    [rawCurrencyCodeList?.data]
  );

  const bankList = useMemo(
    () =>
      rawBankList?.data?.map((b) => ({
        label: b.name,
        value: b.code,
      })),
    [rawBankList?.data]
  );

  useEffect(() => {
    if (isOpen && initialData) {
      reset({
        merchantId: initialData.merchantId,
        account1: {
          currencyCode: initialData.currencyCode?.toString() || "",
          iban: initialData.iban || "",
          bankCode: initialData.bankCode || "",
          accountOwner: initialData.accountOwner || "",
        },
        account2: initialData.iban1
          ? {
            currencyCode: initialData.currencyCode1?.toString() || "",
            iban: initialData.iban1 || "",
            bankCode: initialData.bankCode1 || "",
            accountOwner: initialData.accountOwner1 || "",
          }
          : undefined,
        account3: initialData.iban2
          ? {
            currencyCode: initialData.currencyCode2?.toString() || "",
            iban: initialData.iban2 || "",
            bankCode: initialData.bankCode2 || "",
            accountOwner: initialData.accountOwner2 || "",
          }
          : undefined,
      });

      let count = 1;
      if (initialData.iban1) count++;
      if (initialData.iban2) count++;
      setAccountsCount(count);
    } else if (!isOpen) {
      reset({});
      setAccountsCount(1);
    }
  }, [isOpen, initialData]);

  const addAccount = () => {
    if (accountsCount < 3) setAccountsCount(accountsCount + 1);
  };

  const removeAccount = (index: number) => {
    setValue(`account${index}`, undefined, { shouldDirty: true, shouldValidate: false });
    setAccountsCount((c) => (c === index ? c - 1 : c));
  };

  const onSubmit = (data: FormValues) => {
    if (!initialData?.id) return;

    const payload = {
      id: initialData.id,
      merchantId: data.merchantId,
      currencyCode: Number(data.account1.currencyCode),
      iban: data.account1.iban,
      bankCode: data.account1.bankCode,
      accountOwner: data.account1.accountOwner,
      bankType: 1,

      currencyCode1: data.account2?.currencyCode ? Number(data.account2.currencyCode) : 0,
      iban1: data.account2?.iban || "",
      bankCode1: data.account2?.bankCode || "",
      accountOwner1: data.account2?.accountOwner || "",
      bankType1: data.account2?.iban ? 1 : 0,

      currencyCode2: data.account3?.currencyCode ? Number(data.account3.currencyCode) : 0,
      iban2: data.account3?.iban || "",
      bankCode2: data.account3?.bankCode || "",
      accountOwner2: data.account3?.accountOwner || "",
      bankType2: data.account3?.iban ? 1 : 0,
    };

    merchantBankUpdate(payload, {
      onSuccess: (res) => {
        if (res.isSuccess) {
          setSnackbar({
            severity: "success",
            isOpen: true,
            description: res.message,
          });
          onSaveSuccess();
          onClose();
        } else {
          setSnackbar({
            severity: "error",
            isOpen: true,
            description: res.message,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          isOpen: true,
          description: "Banka hesabı güncellenirken bir hata oluştu.",
        });
      },
    });
  };

  const renderAccounts = () => {
    const arr = [];
    for (let i = 1; i <= accountsCount; i++) {
      arr.push(
        <React.Fragment key={i}>
          {i > 1 && <Divider sx={{ my: 2 }} />}
          <AccountInput
            id={i}
            currencyCodeField={`account${i}.currencyCode`}
            ibanField={`account${i}.iban`}
            bankCodeField={`account${i}.bankCode`}
            accountOwnerField={`account${i}.accountOwner`}
            control={control}
            currencyCodeList={currencyCodeList}
            bankList={bankList}
            setValue={setValue}
            isDesktop={isDesktop}
            onRemove={() => removeAccount(i)}
          />
        </React.Fragment>
      );
    }
    return arr;
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      {isLoading && <Loading />}
      <DialogTitle>Banka Hesabını Düzenle</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            {initialData?.merchantId && (
              <TextField
                label="Üye İşyeri"
                value={`${initialData.merchantName} (${initialData.merchantId})`}
                disabled
                fullWidth
              />
            )}
          </FormControl>

          {renderAccounts()}

          <MuiButton
            variant="outlined"
            onClick={addAccount}
            disabled={accountsCount >= 3}
            sx={{ mt: 2 }}
          >
            + Hesap Ekle
          </MuiButton>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} color="secondary">
          İptal
        </MuiButton>
        <Button text="Kaydet" onClick={handleSubmit(onSubmit)} />
      </DialogActions>
    </Dialog>
  );
};
